import {
  ORIGENS_DE_CANCELAMENTO,
  agregadoDoCriterioSchema,
  relatorioDaPesquisaSchema,
  type RelatorioDaPesquisa,
} from '@locaveri/shared'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPatch, apiPost, apiPut } from '@/lib/api'
import { ROTAS_DE_PESQUISA } from '@/lib/endpoints'

/**
 * PESQUISA DE SATISFAÇÃO e MOTIVOS DE CANCELAMENTO — a camada de dados.
 *
 * ===========================================================================
 * A MATEMÁTICA NÃO MORA AQUI, E ISSO É REGRA
 * ===========================================================================
 * NPS, faixas, zonas e taxa de resposta vêm de `@locaveri/shared/pesquisa`, e a
 * API já devolve tudo calculado. Nada neste arquivo recalcula nenhum dos dois —
 * um segundo cálculo no painel é exatamente como a tela e o relatório passam a
 * discordar sobre o mesmo número, e ninguém descobre qual dos dois está certo.
 *
 * O que a tela FAZ com o número (cor, rótulo da zona, ordem) é apresentação e
 * mora nos componentes.
 */

// ---------------------------------------------------------------------------
// Configuração
// ---------------------------------------------------------------------------

export const criterioDaApiSchema = z.object({
  id: z.uuid(),
  rotulo: z.string(),
  ordem: z.number().int(),
  ativo: z.boolean(),
  /** Quantas respostas já apontam para ele — a tela avisa antes de renomear. */
  respostas: z.number().int().nonnegative(),
})

export type CriterioDaApi = z.infer<typeof criterioDaApiSchema>

export const configuracaoDaApiSchema = z.object({
  ativa: z.boolean(),
  escalaMinima: z.number().int(),
  escalaMaxima: z.number().int(),
  rotuloDaNotaMinima: z.string(),
  rotuloDaNotaMaxima: z.string(),
  minimaDePromotor: z.number().int().optional(),
  minimaDePassivo: z.number().int().optional(),
  notaMinimaParaReview: z.number().int().optional(),
  urlDeReview: z.string().nullish(),
  mensagemDeRedirecionamento: z.string(),
  emailParaFeedbackNegativo: z.string().nullish(),
  criterios: z.array(criterioDaApiSchema),
  /** Já RESOLVIDOS pela API — a tela mostra o corte que está valendo. */
  minimaDePromotorEfetiva: z.number().int(),
  minimaDePassivoEfetiva: z.number().int(),
})

export type ConfiguracaoDaApi = z.infer<typeof configuracaoDaApiSchema>

export interface CriterioParaSalvar {
  /** Ausente = critério NOVO. Presente = renomeia/reordena o existente. */
  readonly id?: string
  readonly rotulo: string
  readonly ordem: number
  readonly ativo: boolean
}

export interface ConfiguracaoParaSalvar {
  readonly ativa: boolean
  readonly escalaMinima: number
  readonly escalaMaxima: number
  readonly rotuloDaNotaMinima: string
  readonly rotuloDaNotaMaxima: string
  readonly notaMinimaParaReview?: number
  readonly urlDeReview?: string | null
  readonly mensagemDeRedirecionamento: string
  readonly emailParaFeedbackNegativo?: string | null
  readonly criterios: readonly CriterioParaSalvar[]
}

export const CHAVE_DA_CONFIGURACAO = ['pesquisa', 'config'] as const
export const CHAVE_DO_RELATORIO = ['pesquisa', 'relatorio'] as const
export const CHAVE_DOS_MOTIVOS = ['pesquisa', 'motivos'] as const

export function useConfiguracaoDaPesquisa(): UseQueryResult<ConfiguracaoDaApi> {
  return useQuery({
    queryKey: CHAVE_DA_CONFIGURACAO,
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PESQUISA.configuracao, configuracaoDaApiSchema, { signal }),
  })
}

/**
 * Salva a configuração INTEIRA.
 *
 * `PUT` e não `PATCH`: a lista enviada passa a SER a configuração. Critério que
 * não vem na lista é desativado pela API — nunca apagado, porque as respostas
 * antigas apontam para ele.
 */
export function useSalvarConfiguracao(): UseMutationResult<
  ConfiguracaoDaApi,
  Error,
  ConfiguracaoParaSalvar
> {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: (dados: ConfiguracaoParaSalvar) =>
      apiPut(ROTAS_DE_PESQUISA.configuracao, configuracaoDaApiSchema, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DA_CONFIGURACAO })
      // O relatório também: mexer nos limiares muda o NPS de respostas que já
      // existiam, e deixar o número velho na tela é pior que recarregá-lo.
      void cliente.invalidateQueries({ queryKey: CHAVE_DO_RELATORIO })
    },
  })
}

// ---------------------------------------------------------------------------
// Relatório
// ---------------------------------------------------------------------------

export interface FiltroDoRelatorio {
  readonly de?: string
  readonly ate?: string
  readonly locationId?: string
}

export function useRelatorioDaPesquisa(
  filtro: FiltroDoRelatorio,
): UseQueryResult<RelatorioDaPesquisa> {
  return useQuery({
    queryKey: [...CHAVE_DO_RELATORIO, filtro],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PESQUISA.relatorio(filtro), relatorioDaPesquisaSchema, { signal }),
  })
}

export { agregadoDoCriterioSchema }

// ---------------------------------------------------------------------------
// Motivos de cancelamento
// ---------------------------------------------------------------------------

export const motivoDaApiSchema = z.object({
  id: z.uuid(),
  rotulo: z.string(),
  origem: z.enum(ORIGENS_DE_CANCELAMENTO),
  ordem: z.number().int(),
  ativo: z.boolean(),
  /** Quantas transições já usaram este motivo. Desativar nunca apaga. */
  usos: z.number().int().nonnegative(),
})

export type MotivoDaApi = z.infer<typeof motivoDaApiSchema>

const listaDeMotivosSchema = z.object({ itens: z.array(motivoDaApiSchema) })

export interface MotivoParaSalvar {
  readonly rotulo: string
  readonly origem: (typeof ORIGENS_DE_CANCELAMENTO)[number]
  readonly ordem: number
  readonly ativo: boolean
}

/**
 * O catálogo.
 *
 * `incluirInativos` é `true` na tela de configuração porque desativar não
 * apaga: sem ver o que está desligado, o gestor não tem como religar — e
 * criaria um item novo com o mesmo nome, partindo a série histórica em duas.
 */
export function useMotivosDeCancelamento(opcoes: {
  readonly incluirInativos?: boolean
  readonly origem?: 'CLIENTE' | 'EMPRESA'
}): UseQueryResult<{ readonly itens: readonly MotivoDaApi[] }> {
  return useQuery({
    queryKey: [...CHAVE_DOS_MOTIVOS, opcoes],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_PESQUISA.motivos(opcoes), listaDeMotivosSchema, { signal }),
  })
}

export function useCriarMotivo(): UseMutationResult<MotivoDaApi, Error, MotivoParaSalvar> {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: (dados: MotivoParaSalvar) =>
      apiPost(ROTAS_DE_PESQUISA.criarMotivo, motivoDaApiSchema, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DOS_MOTIVOS })
    },
  })
}

export function useAtualizarMotivo(): UseMutationResult<
  MotivoDaApi,
  Error,
  { readonly id: string; readonly dados: MotivoParaSalvar }
> {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: { readonly id: string; readonly dados: MotivoParaSalvar }) =>
      apiPatch(ROTAS_DE_PESQUISA.motivo(id), motivoDaApiSchema, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DOS_MOTIVOS })
    },
  })
}
